import { useState } from "react";
import { Plus, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { TextField, Label, ChoiceGroup } from "./Fields";
import { PetCard } from "./PetCard";
import { emptySurvey, emptyPet, validateSurvey, toPayload, INTERES } from "./constants";
import { formatApiErrorDetail } from "@/lib/api";

const Section = ({ letter, title, subtitle, children, testId }) => (
  <section data-testid={testId} className="space-y-4">
    <div className="flex items-start gap-3">
      <span className="grid place-items-center w-9 h-9 rounded-xl bg-coral text-white font-display font-bold shrink-0">{letter}</span>
      <div>
        <h2 className="font-display text-base md:text-lg font-bold text-charcoal">{title}</h2>
        {subtitle && <p className="text-sm text-muted-moss">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

export const SurveyForm = ({ onSubmit, submitLabel = "Enviar encuesta" }) => {
  const [form, setForm] = useState(emptySurvey);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (Object.keys(errors).length) setErrors(validateSurvey(next));
    return next;
  });
  const setPet = (i, p) => set("mascotas", form.mascotas.map((m, j) => (j === i ? p : m)));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateSurvey(form);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Completa los campos obligatorios marcados en rojo");
      setTimeout(() => document.querySelector(".border-red-400")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setSending(true);
    try {
      await onSubmit(toPayload(form));
      toast.dismiss();
      setForm(emptySurvey());
      setErrors({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <form data-testid="cf-form" onSubmit={submit} noValidate className="space-y-10">
      <Section letter="A" title="Tus datos de contacto" testId="cf-section-a">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField label="Nombre completo" testId="cf-nombre" value={form.nombre} onChange={(e) => set("nombre", e.target.value)} error={errors.nombre} placeholder="Ej: María Rodríguez" autoComplete="name" />
          <TextField errorText="Debe tener 8 dígitos" label="Teléfono / WhatsApp" testId="cf-telefono" type="tel" inputMode="numeric" maxLength={9} value={form.telefono}
              onChange={(e) => set("telefono", e.target.value.replace(/[^\d-]/g, ""))} error={errors.telefono} placeholder="8888-8888" autoComplete="tel" />
          <TextField label="Zona donde vives" testId="cf-zona" className="sm:col-span-2" value={form.zona} onChange={(e) => set("zona", e.target.value)} error={errors.zona} placeholder="Ej: Escazú, San José" />
        </div>
      </Section>

      <Section letter="B" title="Tus mascotas y su alimento" subtitle="Agrega todas tus mascotas y todos los alimentos que les das." testId="cf-section-b">
        <div className="space-y-4">
          {form.mascotas.map((m, i) => (
            <PetCard key={m._k} pet={m} pi={i} errors={errors} onChange={(p) => setPet(i, p)}
              onRemove={() => set("mascotas", form.mascotas.filter((_, j) => j !== i))} canRemove={form.mascotas.length > 1} />
          ))}
          <button type="button" data-testid="cf-add-pet" onClick={() => set("mascotas", [...form.mascotas, emptyPet()])}
            className="w-full h-12 rounded-2xl bg-sage/60 text-charcoal font-semibold inline-flex items-center justify-center gap-2 hover:bg-sage active:scale-[0.98] transition-transform">
            <Plus className="w-4 h-4" /> Agregar otra mascota
          </button>
        </div>
      </Section>

      <Section letter="C" title="Prueba Lenda" subtitle="Lenda es nuestro nuevo alimento. ¿Te gustaría probarlo con tu mascota?" testId="cf-section-c">
        <div>
          <Label>¿Te interesa recibir una prueba?</Label>
          <ChoiceGroup testId="cf-interes" options={INTERES} value={form.interes_prueba} onChange={(v) => set("interes_prueba", v)} error={errors.interes_prueba} />
          {errors.interes_prueba && <span className="block mt-1 text-xs text-red-500">Campo obligatorio</span>}
        </div>
        <label className="block">
          <Label optional>Comentarios u observaciones</Label>
          <textarea data-testid="cf-observaciones" rows={3} value={form.observaciones} onChange={(e) => set("observaciones", e.target.value)}
            placeholder="Alergias, preferencias, algo que quieras contarnos…"
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#E3E7E2] text-[16px] text-charcoal focus:outline-none focus:ring-2 focus:ring-coral/40" />
        </label>
      </Section>

      <button type="submit" data-testid="cf-submit" disabled={sending}
        className="w-full h-14 rounded-2xl bg-coral text-white font-bold text-base inline-flex items-center justify-center gap-2 hover:bg-coral-dark active:scale-[0.98] transition-transform disabled:opacity-60">
        {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} {submitLabel}
      </button>
    </form>
  );
};
